import { useEffect } from "react";
import type { ReceiptDraft } from "../../types/receipt";
import type { DashboardData, Transaction } from "../../types";
import { lazy, Suspense } from "react";
import { receiptLinePriceRepair } from "../../lib/receipt-price-reconciliation";
import { receiptSplitExpense } from "../../lib/receipt-splits";
import { isExpense, money } from "../../lib/finance";
import { appendReceiptFiles } from "../../lib/image-data";
import LoadingLabel from "../../components/LoadingLabel";
import ReceiptCapture from "./ReceiptCapture";
import ReceiptReviewItems from "./ReceiptReviewItems";
import { useTransactionReceipt } from "./use-transaction-receipt";

const ReceiptCategorySplit = lazy(() => import("../../components/ReceiptCategorySplit"));

export default function TransactionReceiptForm({ transaction, sourceReceiptId, onSaved, onCancel, onBusyChange, entryOptions }: {
  transaction: Transaction;
  sourceReceiptId?: string;
  onSaved: () => void;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
  entryOptions?: DashboardData["entryOptions"];
}) {
  const flow = useTransactionReceipt(transaction, sourceReceiptId, onSaved);
  const { state, busy, editDraft } = flow;
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);
  return <section className="receipt-ui transaction-receipt-form" aria-label="Attach receipt" onPaste={event => {
    if (state || busy) return;
    const files = Array.from(event.clipboardData.files).filter(file => file.type.startsWith("image/"));
    if (files.length) { event.preventDefault(); flow.setAttachments(current => appendReceiptFiles(current, files)); }
  }}>
    <h3>{sourceReceiptId ? "Read attached receipt" : "Attach receipt"}</h3>
    <p className="muted">For {transaction.title || transaction.description} · {money(transaction.amount, transaction.currency)}. Your saved amount and account stay the same.</p>
    {!state ? sourceReceiptId ? <>
      <p className="smart-caption">Reading sends the attached photos to an AI provider (OpenAI or Gemini). Review the extracted details before saving.</p>
      <button type="button" className="primary-button" disabled={busy} onClick={() => void flow.scan()}>{busy ? <LoadingLabel>Reading receipt</LoadingLabel> : "Read attached photos"}</button>
    </> : <ReceiptCapture attachments={flow.attachments} setAttachments={flow.setAttachments} busy={busy}
      onScan={() => void flow.scan()} onAttachPhotos={() => void flow.scan(false)} setError={flow.setError} /> : <>
      <p className="muted">Receipt {state.receiptNumber || 1} of {state.receiptTotal || 1} · {state.draft.items.length} items</p>
      {state.draft.warning && <p className="receipt-warning">{state.draft.warning}</p>}
      {!!state.draft.items.length && <>
        <fieldset disabled={busy || state.pending}>
          <label>Receipt shop<input value={state.draft.expense.title} maxLength={160} onChange={event => editDraft({ expense: { ...state.draft.expense, title: event.target.value } })} /></label>
          <div className="expense-fields">
            <label>Receipt total<input inputMode="decimal" value={state.draft.expense.amount} onChange={event => editDraft({ expense: { ...state.draft.expense, amount: event.target.value } })} /></label>
            <label>Receipt currency<input value={state.draft.currency} maxLength={8} placeholder={transaction.currency} onChange={event => editDraft({ currency: event.target.value.toUpperCase() })} /></label>
          </div>
        </fieldset>
        {(state.draft.currency !== transaction.currency || Number(state.draft.expense.amount) !== transaction.amount) &&
          <p className="receipt-warning">The receipt total or currency differs from this transaction. Check that this is the right receipt. Attaching it keeps the saved transaction amount.</p>}
        <ReceiptReviewItems state={state} busy={busy} editState={flow.editState} editDraft={editDraft} allowTracking={isExpense(transaction)} />
        {isExpense(transaction) && entryOptions && <Suspense fallback={<p className="muted">Loading receipt categories…</p>}><ReceiptCategorySplit receipt={state.draft} expense={state.draft.expense} options={entryOptions} disabled={busy || state.pending} onChange={(categorySplit, expense, repairLinePrices) => {
          let draft: ReceiptDraft = { ...state.draft, categorySplit, expense: expense || state.draft.expense };
          if (repairLinePrices) {
            const repaired = receiptLinePriceRepair(draft);
            if (!repaired) return;
            draft = repaired;
          }
          try { draft.expense = receiptSplitExpense(draft); } catch { /* Keep incomplete choices for review. */ }
          editDraft(draft);
        }} /></Suspense>}
        {state.draft.categorySplit && state.draft.categorySplit.mode !== "off" && <p className="muted">This saves the receipt’s category choices. Use “Review transaction with receipt details” afterwards to review and save the transaction categories.</p>}
      </>}
      <button type="button" className="primary-button" disabled={busy} onClick={() => void flow.save()}>
        {busy ? <LoadingLabel>Saving receipt</LoadingLabel> : state.pending ? "Retry receipt attachment" : "Save receipt & selected prices"}
      </button>
      {!state.pending && <button type="button" className="text-button" disabled={busy} onClick={flow.discard}>Discard receipt draft</button>}
    </>}
    {flow.error && <p className="form-error" role="alert">{flow.error}</p>}
    <button type="button" className="text-button" disabled={busy} onClick={onCancel}>{state ? "Close receipt review · draft kept" : "Cancel"}</button>
  </section>;
}
